import {
  Assignment as AssignmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  CalendarToday as CalendarIcon,
  Chat as ChatIcon,
  Flag as FlagIcon,
  LocationOn as LocationIcon,
  Message as MessageIcon,
  NewReleases as NewReleasesIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Paper, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgentAvatar } from "../components/AgentAvatar";
import { HelpIcon } from "../components/HelpIcon";
import { ReleaseNotesModal } from "../components/ReleaseNotesModal";
import { tutorials } from "../config/tutorials";
import { useReleaseNotes } from "../hooks/useReleaseNotes";
import { usePageTutorial } from "../hooks/useTutorial";
import { AgentMessagingService } from "../services/AgentMessagingService";
import { calendarService } from "../services/CalendarService";
import { PriorityService } from "../services/PriorityService";
import { Todo, TodoService } from "../services/TodoService";
import { Priority } from "../types";
import { CalendarEvent } from "../types/calendar";
import { DateUtils } from "../utils/dateUtils";
import { TimezoneUtils } from "../utils/timezoneUtils";
import { userProfileService } from "../services/UserProfileService";

// Basic markdown renderer (bold, italics, links, line breaks)
const renderMarkdown = (text: string) => {
  if (!text) return null;
  // Replace **bold**
  let html = text.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
  // Replace *italic*
  html = html.replace(/\*(.*?)\*/g, "<i>$1</i>");
  // Replace [text](url)
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // Replace line breaks
  html = html.replace(/\n/g, "<br/>");
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
};

interface AgentMessage {
  id: string;
  agentName: string;
  message: string;
  timestamp: Date;
  threadId: string;
  avatarPath?: string;
}

interface TodoItem {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  priority: "low" | "medium" | "high";
  deadline?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// Helper function to convert Firebase timestamp to Date
const convertFirebaseTimestamp = (timestamp: any): Date => {
  if (timestamp && typeof timestamp === "object" && timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === "string" || typeof timestamp === "number") {
    return new Date(timestamp);
  }
  return new Date();
};

export const OverviewPage: React.FC = () => {
  const navigate = useNavigate();

  // Individual loading states
  const [eventsLoading, setEventsLoading] = useState(true);
  const [agentMessagesLoading, setAgentMessagesLoading] = useState(true);
  const [todosLoading, setTodosLoading] = useState(true);
  const [prioritiesLoading, setPrioritiesLoading] = useState(true);

  // Data states
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [agentMessages, setAgentMessages] = useState<AgentMessage[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);

  // UI states
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState<string | null>(null);
  const [userTimezone, setUserTimezone] = useState<string>("");

  // Initialize tutorial for this page
  usePageTutorial("overview_page_tour", tutorials.overview);

  // Get release notes for "What's New" button
  const { hasNewReleases } = useReleaseNotes();

  // Load user timezone
  useEffect(() => {
    const loadUserTimezone = async () => {
      try {
        const profile = await userProfileService.getUserProfile();
        if (profile?.timezone) {
          setUserTimezone(profile.timezone);
        } else {
          // Fallback to browser timezone
          setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error("Error loading user timezone:", error);
        // Fallback to browser timezone
        setUserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    };

    loadUserTimezone();
  }, []);

  // Async loading functions
  const loadEvents = async () => {
    try {
      setEventsLoading(true);
      const currentTime = new Date();
      const oneDayAgo = new Date(currentTime);
      oneDayAgo.setDate(currentTime.getDate() - 1);
      const nextWeek = new Date(currentTime);
      nextWeek.setDate(currentTime.getDate() + 7);

      const calendarEvents = await calendarService.getEvents({
        timeRange: { start: oneDayAgo, end: nextWeek },
        limit: 50,
      });

      const convertedEvents: CalendarEvent[] = calendarEvents.map((event: any) => ({
        ...event,
        startTime: event.startTime instanceof Date ? event.startTime : new Date(event.startTime),
        endTime: event.endTime instanceof Date ? event.endTime : new Date(event.endTime),
        visibility: event.visibility || "default",
      }));

      const filterTime = new Date();
      const relevantEvents = convertedEvents.filter((event) => {
        const isCurrentlyInProgress = event.startTime <= filterTime && event.endTime >= filterTime;
        const isFutureEvent = event.startTime > filterTime;
        const isTodayAndNotEnded = event.startTime.toDateString() === filterTime.toDateString() && event.endTime >= filterTime;
        return isCurrentlyInProgress || isFutureEvent || isTodayAndNotEnded;
      });

      setEvents(relevantEvents);
    } catch (error) {
      console.error("Error loading events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadAgentMessages = async () => {
    try {
      setAgentMessagesLoading(true);
      const agentChatInfos = await AgentMessagingService.getAgentChatInfo();

      const messages: AgentMessage[] = agentChatInfos
        .filter((info) => info.lastMessage && info.lastMessageTime)
        .map((info) => ({
          id: `overview_${info.agentId}`,
          agentName: info.agentName,
          message: info.lastMessage!,
          timestamp: info.lastMessageTime!,
          threadId: info.threadId,
          avatarPath: info.avatarPath,
        }))
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      setAgentMessages(messages.slice(0, 3));
    } catch (error) {
      console.error("Error loading agent messages:", error);
    } finally {
      setAgentMessagesLoading(false);
    }
  };

  const loadTodos = async () => {
    try {
      setTodosLoading(true);
      const todoService = new TodoService();
      const todoItems = await todoService.getTodos();

      const convertedTodos: TodoItem[] = todoItems.map((todo: any) => ({
        ...todo,
        deadline: todo.deadline ? (todo.deadline instanceof Date ? todo.deadline : new Date(todo.deadline)) : undefined,
        createdAt: todo.createdAt instanceof Date ? todo.createdAt : new Date(todo.createdAt),
        updatedAt: todo.updatedAt instanceof Date ? todo.updatedAt : new Date(todo.updatedAt),
      }));

      const sortedTodos = convertedTodos.sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] || 0;
        const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] || 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority;
        }

        if (a.deadline && b.deadline) {
          return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
        }
        if (a.deadline && !b.deadline) return -1;
        if (!a.deadline && b.deadline) return 1;

        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      });

      setTodos(sortedTodos.slice(0, 5));
    } catch (error) {
      console.error("Error loading todos:", error);
    } finally {
      setTodosLoading(false);
    }
  };

  const loadPriorities = async () => {
    try {
      setPrioritiesLoading(true);
      const priorityService = new PriorityService();
      const priorityItems = await priorityService.getPriorities();
      setPriorities(priorityItems.slice(0, 3));
    } catch (error) {
      console.error("Error loading priorities:", error);
    } finally {
      setPrioritiesLoading(false);
    }
  };

  // Load all data asynchronously
  useEffect(() => {
    loadEvents();
    loadAgentMessages();
    loadTodos();
    loadPriorities();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadEvents(), loadAgentMessages(), loadTodos(), loadPriorities()]);
    setRefreshing(false);
  };

  // Handler functions
  const handleChatWithAgent = async (agentName: string) => {
    try {
      const agentChatInfos = await AgentMessagingService.getAgentChatInfo();
      const agent = agentChatInfos.find((a) => a.agentName.toLowerCase() === agentName.toLowerCase() || a.agentType.toLowerCase().includes(agentName.toLowerCase()));

      if (agent) {
        navigate(`/chat/${agent.agentId}`);
      } else {
        console.error(`Agent ${agentName} not found`);
        setError(`Agent ${agentName} not found`);
      }
    } catch (error) {
      console.error("Error finding agent:", error);
      setError("Failed to find agent");
    }
  };

  const handleAIAssist = async (type: "todo" | "priority", item: Todo | Priority) => {
    try {
      setAiLoading(item.id);

      // Find the PersonalAssistant agent
      const agentChatInfos = await AgentMessagingService.getAgentChatInfo();
      const personalAssistantAgent = agentChatInfos.find((a) => a.agentType === "PersonalAssistantAgent");

      if (!personalAssistantAgent) {
        throw new Error("PersonalAssistant agent not found");
      }

      let message = "";
      if (type === "todo") {
        const todo = item as Todo;
        const createdDate = DateUtils.formatForMessage(todo.createdAt);
        const deadlineDate = todo.deadline ? DateUtils.formatForMessage(todo.deadline) : null;
        message = `Please help me with this todo: "${todo.title}"${todo.todoName ? ` (also known as: ${todo.todoName})` : ""}. Priority: ${todo.priority}${deadlineDate ? `, Deadline: ${deadlineDate}` : ""}. ${todo.completed ? "This todo is completed." : "This todo is pending."} Created: ${createdDate}.`;
      } else {
        const priority = item as Priority;
        const createdDate = DateUtils.formatForMessage(priority.createdAt);
        message = `Please help me review this priority: "${priority.title}"${priority.priorityName ? ` (also known as: ${priority.priorityName})` : ""} (${priority.category}, order: ${priority.order}). ${priority.description ? `Description: ${priority.description}` : ""} Created: ${createdDate}.`;
      }

      // Fire and forget API call
      AgentMessagingService.sendUserMessageToAgent(personalAssistantAgent.agentId, message).catch((error) => {
        console.error("Error sending AI assist message:", error);
      });

      // Navigate to chat with PersonalAssistant
      navigate(`/chat/${personalAssistantAgent.agentId}`);
    } catch (error) {
      console.error("Error initiating AI assist:", error);
      setError("Failed to start AI assistance");
    } finally {
      setAiLoading(null);
    }
  };

  const formatEventTime = (date: Date) => {
    if (!DateUtils.isValidDate(date)) return "Invalid time";

    if (userTimezone) {
      // The date might already be in the user's timezone or event timezone
      // Use DateUtils.formatTime which handles timezone conversion properly
      return DateUtils.formatTime(date, userTimezone);
    }
    return DateUtils.formatTime(date);
  };

  const formatEventDate = (date: Date) => {
    if (!DateUtils.isValidDate(date)) return "Invalid date";

    if (userTimezone) {
      // Use DateUtils.formatRelative which handles timezone conversion properly
      return DateUtils.formatRelative(date, userTimezone);
    }
    return DateUtils.formatRelative(date);
  };

  const isEventCurrentlyInProgress = (event: CalendarEvent) => {
    const now = new Date();
    return event.startTime <= now && event.endTime >= now;
  };

  const groupEventsByDate = (events: CalendarEvent[]) => {
    const grouped: { [key: string]: CalendarEvent[] } = {};

    events.forEach((event) => {
      if (DateUtils.isValidDate(event.startTime)) {
        const dateKey = DateUtils.formatForDisplay(event.startTime);
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(event);
      }
    });

    // Sort events within each day by start time
    Object.keys(grouped).forEach((dateKey) => {
      grouped[dateKey].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    });

    return grouped;
  };

  const sortedDates = Object.keys(groupEventsByDate(events)).sort((a, b) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);

    // Parse the date strings back to Date objects for comparison
    const dateA = new Date(a);
    const dateB = new Date(b);

    // Normalize dates to start of day for comparison
    const normalizedDateA = new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
    const normalizedDateB = new Date(dateB.getFullYear(), dateB.getMonth(), dateB.getDate());

    // Priority order: Today first, Tomorrow second, then chronological
    if (normalizedDateA.getTime() === today.getTime() && normalizedDateB.getTime() !== today.getTime()) {
      return -1; // Today comes first
    }
    if (normalizedDateB.getTime() === today.getTime() && normalizedDateA.getTime() !== today.getTime()) {
      return 1; // Today comes first
    }
    if (normalizedDateA.getTime() === tomorrow.getTime() && normalizedDateB.getTime() !== tomorrow.getTime() && normalizedDateB.getTime() !== today.getTime()) {
      return -1; // Tomorrow comes second (after Today)
    }
    if (normalizedDateB.getTime() === tomorrow.getTime() && normalizedDateA.getTime() !== tomorrow.getTime() && normalizedDateA.getTime() !== today.getTime()) {
      return 1; // Tomorrow comes second (after Today)
    }

    // For all other dates, sort chronologically
    return normalizedDateA.getTime() - normalizedDateB.getTime();
  });

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h4" component="h1">
            Overview
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<NewReleasesIcon />}
              onClick={() => setReleaseNotesOpen(true)}
              color={hasNewReleases ? "primary" : "inherit"}
              sx={{
                ...(hasNewReleases && {
                  borderColor: "primary.main",
                  backgroundColor: "primary.50",
                  "&:hover": {
                    backgroundColor: "primary.100",
                  },
                }),
              }}
            >
              What&apos;s New
            </Button>
            <HelpIcon
              tutorialConfig={tutorials.overview}
              pageDescription="Your dashboard shows your agenda, agent messages, priorities, and todos. Everything you need to stay organized and productive!"
            />
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Your agenda and latest agent updates
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3 }}>
        {/* Calendar Agenda */}
        <Box sx={{ flex: { xs: "1", lg: "1 1 40%" } }}>
          <Paper sx={{ p: 3, height: "100%" }} data-tutorial="agenda-section">
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <CalendarIcon sx={{ mr: 2, color: "primary.main" }} />
              <Typography variant="h5" component="h2">
                Agenda
              </Typography>
            </Box>

            {eventsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : events.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No current or upcoming events
              </Typography>
            ) : (
              <Box>
                {sortedDates.map((dateKey) => {
                  const date = new Date(dateKey);
                  const dayEvents = groupEventsByDate(events)[dateKey];

                  return (
                    <Box key={dateKey} sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, color: "primary.main" }}>
                        {formatEventDate(date)}
                      </Typography>

                      {dayEvents.map((event) => {
                        const isInProgress = isEventCurrentlyInProgress(event);
                        return (
                          <Card
                            key={event.id}
                            sx={{
                              mb: 2,
                              borderLeft: isInProgress ? "4px solid #ff9800" : "4px solid #1976d2",
                              backgroundColor: isInProgress ? "rgba(255, 152, 0, 0.05)" : "inherit",
                            }}
                          >
                            <CardContent sx={{ "padding-top": 2, "padding-bottom": 2, "&:last-child": { pb: 2 } }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <Typography variant="h6" component="h3">
                                      {event.title}
                                    </Typography>
                                    {isInProgress && <Chip label="In Progress" size="small" color="warning" sx={{ fontSize: "0.7rem", height: "20px" }} />}
                                  </Box>

                                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                    <ScheduleIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                                    <Typography variant="body2" color="text.secondary">
                                      {formatEventTime(event.startTime)} - {formatEventTime(event.endTime)}
                                    </Typography>
                                  </Box>

                                  {event.location && (
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                      <LocationIcon sx={{ fontSize: 16, mr: 1, color: "text.secondary" }} />
                                      <Typography variant="body2" color="text.secondary">
                                        {event.location}
                                      </Typography>
                                    </Box>
                                  )}

                                  {event.description && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      {event.description}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Box>

        {/* Agent Messages */}
        <Box sx={{ flex: { xs: "1", lg: "1 1 30%" } }}>
          <Paper sx={{ p: 3, height: "100%" }} data-tutorial="chats-section">
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <MessageIcon sx={{ mr: 2, color: "primary.main" }} />
              <Typography variant="h5" component="h2">
                Chats
              </Typography>
            </Box>

            {agentMessagesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : agentMessages.length === 0 ? (
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                No recent agent messages
              </Typography>
            ) : (
              <List>
                {agentMessages.slice(0, 3).map((message, index) => (
                  <React.Fragment key={message.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <AgentAvatar avatarPath={message.avatarPath} name={message.agentName} size={40} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="subtitle2" component="span">
                              {message.agentName}
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {DateUtils.isValidDate(message.timestamp) ? DateUtils.formatForDisplay(message.timestamp) : "Invalid date"}
                              </Typography>
                              <IconButton size="small" onClick={() => handleChatWithAgent(message.agentName)} sx={{ color: "primary.main" }} title="Chat with this agent">
                                <ChatIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mt: 1,
                            }}
                          >
                            {renderMarkdown(message.message)}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < Math.min(agentMessages.length, 3) - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}

            <Box sx={{ mt: 3 }}>
              <Button variant="outlined" fullWidth onClick={() => navigate("/chat")}>
                View All Chats
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Right Column - Priorities and Todos */}
        <Box sx={{ flex: { xs: "1", lg: "1 1 30%" } }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
            {/* Priorities */}
            <Box sx={{ flex: "1 1 60%" }}>
              <Paper sx={{ p: 3, height: "100%" }} data-tutorial="priorities-section">
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <FlagIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography variant="h5" component="h2">
                    Priorities
                  </Typography>
                </Box>

                {prioritiesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : priorities.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    No priorities found
                  </Typography>
                ) : (
                  <List>
                    {priorities.map((priority, index) => (
                      <React.Fragment key={priority.id}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography variant="body2">{priority.title}</Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip label={priority.category} size="small" color="primary" />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAIAssist("priority", priority)}
                                    disabled={aiLoading === priority.id}
                                    sx={{ color: "primary.main" }}
                                    title="Review this priority with AI"
                                  >
                                    {aiLoading === priority.id ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                  </IconButton>
                                </Box>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                {priority.description && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {priority.description}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < priorities.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}

                <Box sx={{ mt: 3 }}>
                  <Button variant="outlined" fullWidth onClick={() => (window.location.href = "/priorities")}>
                    View All Priorities
                  </Button>
                </Box>
              </Paper>
            </Box>

            {/* Todo List */}
            <Box sx={{ flex: "1 1 40%" }}>
              <Paper sx={{ p: 3, height: "100%" }} data-tutorial="todos-section">
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <AssignmentIcon sx={{ mr: 2, color: "primary.main" }} />
                  <Typography variant="h5" component="h2">
                    Top Todos
                  </Typography>
                </Box>

                {todosLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : todos.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                    No todos found
                  </Typography>
                ) : (
                  <List>
                    {todos.map((todo, index) => (
                      <React.Fragment key={todo.id}>
                        <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration: todo.completed ? "line-through" : "none",
                                    color: todo.completed ? "text.secondary" : "text.primary",
                                  }}
                                >
                                  {todo.title}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Chip label={todo.priority} size="small" color={todo.priority === "high" ? "error" : todo.priority === "medium" ? "warning" : "default"} />
                                  {todo.deadline && DateUtils.isValidDate(todo.deadline) && (
                                    <Chip
                                      label={DateUtils.formatForDisplay(todo.deadline)}
                                      size="small"
                                      color={DateUtils.isToday(todo.deadline) ? "error" : DateUtils.isTomorrow(todo.deadline) ? "warning" : "default"}
                                    />
                                  )}
                                  <IconButton
                                    size="small"
                                    onClick={() => handleAIAssist("todo", todo)}
                                    disabled={aiLoading === todo.id}
                                    sx={{ color: "primary.main" }}
                                    title="Action this todo with AI"
                                  >
                                    {aiLoading === todo.id ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
                                  </IconButton>
                                </Box>
                              </Box>
                            }
                            secondary={
                              todo.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    mt: 1,
                                  }}
                                >
                                  {todo.description}
                                </Typography>
                              )
                            }
                          />
                        </ListItem>
                        {index < todos.length - 1 && <Divider variant="inset" component="li" />}
                      </React.Fragment>
                    ))}
                  </List>
                )}

                <Box sx={{ mt: 3 }}>
                  <Button variant="outlined" fullWidth onClick={() => (window.location.href = "/todo")}>
                    View All Todos
                  </Button>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Release Notes Modal */}
      <ReleaseNotesModal open={releaseNotesOpen} onClose={() => setReleaseNotesOpen(false)} />
    </Box>
  );
};
